import React, { useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  CircularProgress,
  Typography,
  TablePagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setLimit, setPage } from "../../../redux/refundList/refundListSlice";
import { useGetList } from "../../../hooks/useGetList";
import buildParams from "../../../utils/buildParams";
import formatDate from "../../../utils/formatDate";

const RefundListTable = () => {
  const filter = useSelector((state) => state.refundList);
  const dispatch = useDispatch();
  const validRefundTypes = ["CashBack", "Treatment"];
  const validPaymentTypes = ["Cash", "Bank"];
  const sanitizedFilter = {
    ...filter,
    filterType: {
      ...filter.filterType,
      refundType: validRefundTypes.includes(filter.filterType?.refundType)
        ? filter.filterType.refundType
        : "",
      paymentType: validPaymentTypes.includes(filter.filterType?.paymentType)
        ? filter.filterType.paymentType
        : "",
    },
  };
  const params = buildParams(sanitizedFilter);
  const paramsKey = JSON.stringify(params);
  const lastFetchedKeyRef = useRef(null);

  const { data, loading, fetchData } = useGetList(
    "refund/treatment-vouchers",
    false,
  );

  useEffect(() => {
    if (paramsKey === lastFetchedKeyRef.current) return;
    lastFetchedKeyRef.current = paramsKey;
    fetchData(
      "refund/treatment-vouchers",
      Object.keys(params).length ? params : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };

  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(Number(e.target.value)));
    dispatch(setPage(1));
  };

  const getRefundTypeChip = (type) => {
    return (
      <Chip
        label={type || "N/A"}
        size="small"
        sx={{
          bgcolor: "#26c6da",
          color: "#fff",
          fontWeight: 600,
          fontSize: "11px",
          height: "22px",
        }}
      />
    );
  };

  const getStatusChip = (status) => {
    const colors = {
      Processed: { bgcolor: "#4caf50", color: "#fff" },
      Pending: { bgcolor: "#ff9800", color: "#fff" },
      Cancelled: { bgcolor: "#f44336", color: "#fff" },
    };
    const style = colors[status] || { bgcolor: "#9e9e9e", color: "#fff" };
    return (
      <Chip
        label={status || "N/A"}
        size="small"
        sx={{ ...style, fontWeight: 500, fontSize: "12px" }}
      />
    );
  };

  const rows = data?.data || data?.list || [];
  const total = data?.pagination?.total || data?.metadata?.total || 0;

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Table sx={{ minWidth: 1000 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Voucher Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Refund Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Payment Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Refund Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Refund Account</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: "#1976d2" }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading refund records...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((row, index) => (
                <TableRow
                  key={row._id || index}
                  sx={{
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    {(filter.page - 1) * filter.limit + index + 1}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "12px" }}
                    >
                      {row.code ||
                        row.relatedVoucher?.code ||
                        row.voucherCode ||
                        "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: "13px" }}
                    >
                      {row.relatedPatient?.name || row.customerName || "N/A"}
                    </Typography>
                    {(row.relatedPatient?.phone || row.customerPhone) && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "12px", color: "#666" }}
                      >
                        {row.relatedPatient?.phone || row.customerPhone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "12px" }}>
                    {row.date || row.createdAt
                      ? formatDate(row.date || row.createdAt)
                      : "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>
                    {(row.totalAmount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>{getRefundTypeChip(row.refundType)}</TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>
                    {row.paymentType || row.refundPaymentType || "N/A"}
                  </TableCell>
                  <TableCell
                    sx={{ color: "#2e7d32", fontWeight: 500, fontSize: "13px" }}
                  >
                    {(
                      row.refundAmount ||
                      row.refundPayloads?.totalRefundAmount ||
                      0
                    ).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px" }}>
                    {row.relatedCash?.name ||
                      row.refundAccount ||
                      row.relatedRefundAccount?.subHeader ||
                      "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", maxWidth: 150 }}>
                    {row.refundReason
                      ? row.refundReason.length > 35
                        ? row.refundReason.substring(0, 35) + "..."
                        : row.refundReason
                      : row.reason
                        ? row.reason.length > 35
                          ? row.reason.substring(0, 35) + "..."
                          : row.reason
                        : "N/A"}
                  </TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={11}
                  align="center"
                  sx={{ py: 4, color: "#888" }}
                >
                  No refund records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20, 25, 100]}
        component="div"
        count={total}
        rowsPerPage={filter.limit}
        page={filter.page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          "& .MuiTablePagination-toolbar": {
            display: "flex",
            alignItems: "center",
            minHeight: "52px",
          },
          "& .MuiTablePagination-selectLabel": {
            margin: 0,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiTablePagination-displayedRows": {
            margin: 0,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiTablePagination-actions": {
            display: "flex",
            alignItems: "center",
          },
        }}
      />
    </Box>
  );
};

export default RefundListTable;